import React from 'react';
import { Briefcase, Award, Clock, Phone, HardHat, CheckCircle2, Building2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const gerants = [
  {
    name: 'Lathir DIENG',
    role: 'Gérant & Expert Technique',
    badge: { value: '10+', label: "Années d'expérience" },
    image:
      'https://images.unsplash.com/photo-1690356107685-3725367f6f3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwaHlkcmF1bGljJTIwd29ya3Nob3B8ZW58MHx8fHwxNzYzMjE4NDAwfDA&ixlib=rb-4.1.0&q=85',
    description:
      "Avec plus de 10 années d'expérience en hydraulique, mécanique, BTP et logistique technique, Lathir DIENG apporte une expertise reconnue dans la maintenance hydraulique, la gestion d'équipe, l'approvisionnement et le suivi de projets.",
    highlights: [
      { icon: Briefcase, title: 'Spécialiste Hydraulique & Mécanique', sub: 'Maintenance, réfection, confection sur mesure' },
      { icon: Award, title: 'Gestion de Projets BTP', sub: "Suivi, approvisionnement, coordination d'équipe" },
      { icon: Clock, title: 'Disponibilité 24h/24', sub: 'Interventions rapides, qualité et sécurité garanties' },
    ],
    quote:
      "Notre engagement : des interventions de qualité, rapides et sécurisées pour garantir la continuité de votre activité.",
    accent: 'green',
  },
  {
    name: 'Ismael Junior BANGOURA',
    role: 'Ingénieur en Génie Civil',
    badge: { value: '10+', label: "Années d'expérience" },
    image:
      'https://images.pexels.com/photos/11701517/pexels-photo-11701517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    description:
      "Professionnel du secteur du BTP disposant de plus de 10 années d'expérience dans la gestion, la coordination et la réalisation de projets de construction et d'infrastructures. Grâce à son expérience terrain et à sa maîtrise des problématiques techniques, il accompagne le développement d'AGIPLUS CI dans les domaines du BTP, de la construction et des infrastructures.",
    expertise: [
      'Travaux de génie civil',
      'Terrassement',
      'Infrastructures routières',
      'Ouvrages industriels',
      'Gestion de chantiers',
      'Coordination technique',
      'Suivi des travaux',
      'Pilotage de projets',
    ],
    quote:
      "Construire des infrastructures durables, conformes aux normes et livrées dans les délais, au service de nos clients.",
    accent: 'orange',
  },
];

const GerantProfile = ({ gerant, reverse }) => {
  const accent = gerant.accent;
  const borderColor = accent === 'green' ? 'border-green-600' : 'border-orange-500';
  const badgeBg = accent === 'green' ? 'bg-orange-500' : 'bg-green-600';
  const iconBg = accent === 'green' ? 'bg-green-100' : 'bg-orange-100';
  const iconColor = accent === 'green' ? 'text-green-600' : 'text-orange-500';
  const roleColor = accent === 'green' ? 'text-orange-600' : 'text-green-700';
  const btnBg = accent === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600';

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-testid={`gerant-${gerant.name.split(' ')[0].toLowerCase()}`}>
      {/* Image */}
      <div className={`relative ${reverse ? 'lg:order-2' : ''}`}>
        <div className={`aspect-square rounded-2xl overflow-hidden border-4 ${borderColor} shadow-2xl`}>
          <img src={gerant.image} alt={gerant.name} className="w-full h-full object-cover" />
        </div>
        <div className={`absolute -bottom-6 ${reverse ? '-left-6' : '-right-6'} ${badgeBg} text-white p-6 rounded-xl shadow-xl`}>
          <div className="text-3xl font-bold">{gerant.badge.value}</div>
          <div className="text-sm">{gerant.badge.label}</div>
        </div>
      </div>

      {/* Content */}
      <div className={reverse ? 'lg:order-1' : ''}>
        <h3 className="text-3xl font-bold text-gray-900 mb-2">{gerant.name}</h3>
        <p className={`text-xl font-semibold mb-6 ${roleColor}`}>{gerant.role}</p>

        <p className="text-lg text-gray-700 mb-8 leading-relaxed">{gerant.description}</p>

        {gerant.highlights && (
          <div className="space-y-4 mb-8">
            {gerant.highlights.map((h, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`${iconBg} p-2 rounded-lg`}>
                  <h.icon className={`h-5 w-5 ${iconColor}`} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{h.title}</div>
                  <div className="text-gray-600 text-sm">{h.sub}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {gerant.expertise && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {gerant.expertise.map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle2 className={`h-5 w-5 flex-shrink-0 mt-0.5 ${iconColor}`} />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        )}

        <Card className="bg-gradient-to-r from-green-50 to-orange-50 border-none">
          <CardContent className="p-6">
            <p className="text-gray-800 font-medium mb-4">"{gerant.quote}"</p>
            <Button onClick={scrollToContact} className={`text-white ${btnBg}`} data-testid={`contact-gerant-${gerant.name.split(' ')[0].toLowerCase()}`}>
              <Phone className="h-4 w-4 mr-2" />
              Contacter la direction
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const Manager = () => {
  return (
    <section id="gerant" className="py-24 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Les <span className="text-green-600">Gérants</span>
          </h2>
          <p className="text-lg text-gray-700">
            Une gouvernance renforcée associant expertise hydraulique & mécanique et expertise BTP & génie civil.
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-24">
          {gerants.map((g, i) => (
            <GerantProfile key={g.name} gerant={g} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};
